import React, { useCallback, useMemo } from 'react';
import Image from 'next/image';
import { track } from '@eventbrite/legacy-infra.datalayer-library';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { PlaceholderSvg1 } from '@eventbrite/legacy-eds.iconography';
import { useIntl } from 'react-intl';
import { AffiliateCodes, LISTING_HEAP_IDS, STATSIG_EVENT_NAMES } from '@/components/_shared/constants';
import type { OrganizerEvent } from '../../../types';
import { useFormattedEventData } from '@/components/RelatedEvents/RelatedEventCard/hooks/useFormattedEventData';
import { UrgencySignalBadge } from '@/components/UrgencySignals/UrgencySignalBadge';
import { MORE_ORGANIZER_EVENTS_CARD_IMAGE_HEIGHT, MORE_ORGANIZER_EVENTS_CARD_WIDTH } from '../../constants';
import { toRelatedEvent } from '../../../utils';
import styles from './MoreOrganizerEventsRedesignCard.module.scss';

type MoreOrganizerEventsRedesignCardProps = {
  organizerEvent: OrganizerEvent;
  affiliateCode?: AffiliateCodes;
};

const getDestinationUrl = (url?: string, affiliateCode?: AffiliateCodes): string | undefined => {
  if (!url || url === '#') {
    return undefined;
  }

  if (!affiliateCode) {
    return url;
  }

  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('aff', affiliateCode);
  return parsedUrl.toString();
};

export const MoreOrganizerEventsRedesignCard = ({
  organizerEvent,
  affiliateCode,
}: MoreOrganizerEventsRedesignCardProps) => {
  const { formatMessage } = useIntl();
  const relatedEvent = useMemo(() => toRelatedEvent(organizerEvent), [organizerEvent]);
  const formattedData = useFormattedEventData(relatedEvent);

  const destinationUrl = useMemo(
    () => getDestinationUrl(formattedData?.url, affiliateCode),
    [affiliateCode, formattedData?.url],
  );

  const imageSrc = formattedData?.imageUrl || organizerEvent.imageUrl;

  const handleOpenEvent = useCallback(() => {
    track({
      eventName: LISTING_HEAP_IDS.ORGANIZER_MORE_EVENTS_CLICK,
      eventData: {
        event_id: organizerEvent.id,
        event_name: organizerEvent.name,
      },
    });
    logEvent(STATSIG_EVENT_NAMES.LISTING_MORE_ORGANIZER_EVENT_CLICK);
  }, [organizerEvent.id, organizerEvent.name]);

  if (!formattedData) {
    return null;
  }

  const imageElement = imageSrc ? (
    <Image
      alt={formattedData.name || formatMessage({ id: 'Event image', defaultMessage: 'Event image' })}
      height={MORE_ORGANIZER_EVENTS_CARD_IMAGE_HEIGHT}
      src={imageSrc}
      width={MORE_ORGANIZER_EVENTS_CARD_WIDTH}
    />
  ) : (
    <PlaceholderSvg1
      className={styles.imageContent}
      data-testid="fallback-image"
      preserveAspectRatio="xMidYMid slice"
    />
  );

  const cardBody = (
    <>
      <div className={styles.imageWrapper}>
        <div className={styles.imageContent}>{imageElement}</div>
        {formattedData.urgencySignalBadge ? (
          <div className={styles.badge}>
            <UrgencySignalBadge badge={formattedData.urgencySignalBadge} />
          </div>
        ) : null}
      </div>
      <div className={styles.content}>
        {formattedData.name ? <div className={styles.cardTitle}>{formattedData.name}</div> : null}
        {formattedData.formattedEventDateTimeString ? (
          <div className={styles.date}>{formattedData.formattedEventDateTimeString}</div>
        ) : null}
        {formattedData.locationString ? <div className={styles.meta}>{formattedData.locationString}</div> : null}
        {formattedData.priceString ? <div className={styles.price}>{formattedData.priceString}</div> : null}
      </div>
    </>
  );

  const accessibleLabel = `${formatMessage({ id: 'Open event' })}: ${formattedData.name}`;

  const cardContent = destinationUrl ? (
    <a
      aria-label={accessibleLabel}
      className={styles.card}
      data-testid={`more-organizer-events-redesign-card-${organizerEvent.id ?? 'unknown'}`}
      href={destinationUrl}
      onClick={handleOpenEvent}
      rel="noopener noreferrer"
      target="_blank"
    >
      {cardBody}
    </a>
  ) : (
    <article
      aria-label={accessibleLabel}
      className={styles.card}
      data-testid={`more-organizer-events-redesign-card-${organizerEvent.id ?? 'unknown'}`}
    >
      {cardBody}
    </article>
  );

  return cardContent;
};
